import { AddressValidationData } from 'containers/Employment/types';
import { DateTime } from 'luxon';
import { ErrorsDictionary } from 'types/interfaces';
import { ERRORTYPE, addressValidation, requiredValidation } from 'utils/validation';
import {
  EmployerPhoneNumberValidationError,
  EmploymentV2ErrorsDictionary,
  EmploymentV2FormData,
  ProInvolvement,
  ProInvolvementErrorData,
} from './types';
import { extensionNumberRegex, phoneNumberCanadaUSARegex } from 'utils/regex';
import { COUNTRIES } from 'utils/constants';
import { EMPLOYMENT_VALUES } from '../Employment/constants';

export const EMPLOYMENT_ERROR_TYPE: EmploymentV2ErrorsDictionary = {
  ...ERRORTYPE,
  START_DATE_MUST_HAVE_VALID_DATA: { type: 'startDateMustBeInThePast' },
};

export const startOccupationDateValidation = (values: EmploymentV2FormData): ErrorsDictionary => {
  const { startOccupationDate } = values;

  if (!startOccupationDate) {
    return { startOccupationDate: EMPLOYMENT_ERROR_TYPE.PATTERN };
  }

  const date = DateTime.fromISO(startOccupationDate);
  const { month, year } = date;

  if (!month || !year) {
    return { startOccupationDate: EMPLOYMENT_ERROR_TYPE.REQUIRED };
  }

  if (!date.isValid) {
    return { startOccupationDate: EMPLOYMENT_ERROR_TYPE.PATTERN };
  }

  if ((date.diffNow('months').toObject().months || 0) >= 0) {
    return { startOccupationDate: EMPLOYMENT_ERROR_TYPE.START_DATE_MUST_HAVE_VALID_DATA };
  }

  return {};
};

export const employmentPhoneValidation = (values: EmploymentV2FormData): EmployerPhoneNumberValidationError => {
  if (values.employerPhoneNumber === undefined || values.employerPhoneNumber === null) {
    return {};
  }

  const { country, number, extensionNumber } = values.employerPhoneNumber;

  const validationRule: EmployerPhoneNumberValidationError = {};

  if (number && number !== '') {
    if (country === COUNTRIES.CA || country === COUNTRIES.US) {
      if (number.length < 10) {
        validationRule.number = EMPLOYMENT_ERROR_TYPE.PATTERN;
      }

      if (!phoneNumberCanadaUSARegex.test(number)) {
        validationRule.number = EMPLOYMENT_ERROR_TYPE.PATTERN;
      }
    } else {
      if (number.length < 4) {
        validationRule.number = EMPLOYMENT_ERROR_TYPE.PATTERN;
      }
    }

    if (extensionNumber && extensionNumber !== '' && !extensionNumberRegex.test(extensionNumber)) {
      validationRule.extensionNumber = EMPLOYMENT_ERROR_TYPE.PATTERN;
    }
  } else {
    if (extensionNumber && extensionNumber !== '') {
      validationRule.number = EMPLOYMENT_ERROR_TYPE.REQUIRED;
    }
  }

  return validationRule;
};

const validateFirmEmployee = (proInvolvement: ProInvolvement): ProInvolvementErrorData => {
  const errors: ProInvolvementErrorData = {};

  // At least one checkbox must be selected
  if (!proInvolvement.investmentFirmEmployeeInd && !proInvolvement.familyInvestmentFirmEmployeeInd) {
    errors.investmentFirmEmployeeInd = ERRORTYPE.REQUIRED;
    errors.familyInvestmentFirmEmployeeInd = ERRORTYPE.REQUIRED;
  }

  // Validate family firm employee dependent fields
  if (proInvolvement.familyInvestmentFirmEmployeeInd === true) {
    Object.assign(
      errors,
      requiredValidation(proInvolvement, [
        'familyInvestmentFirmEmployeeTypeCd',
        'familyInvestmentFirmEmployeeFullName',
      ]),
    );
  }

  return errors;
};

const validateProfessional = (proInvolvement: ProInvolvement): ProInvolvementErrorData => {
  const errors: ProInvolvementErrorData = {};

  // At least one checkbox must be selected
  if (!proInvolvement.professionalInd && !proInvolvement.referenceProfessionalInd) {
    errors.professionalInd = ERRORTYPE.REQUIRED;
    errors.referenceProfessionalInd = ERRORTYPE.REQUIRED;
  }

  // Validate professional firm name
  if (proInvolvement.professionalInd === true) {
    Object.assign(errors, requiredValidation(proInvolvement, ['professionalSecuritiesFirmName']));
  }

  // Validate reference professional dependent fields
  if (proInvolvement.referenceProfessionalInd === true) {
    const fieldsToValidate = [
      'referenceProfessionalSecuritiesFirmName',
      'referenceProfessionalFullName',
      'referenceProfessionalRelationTypeCd',
    ];

    // Add "Other" relation details field if applicable
    if (proInvolvement.referenceProfessionalRelationTypeCd === EMPLOYMENT_VALUES.TRADING_RELATION_STATUS_OTHER) {
      fieldsToValidate.push('referenceProfessionalRelationTypeDetails');
    }

    Object.assign(errors, requiredValidation(proInvolvement, fieldsToValidate));
  }

  return errors;
};

export const proInvolvementValidation = (values: EmploymentV2FormData): ProInvolvementErrorData | {} => {
  const { proInvolvement, firmEmployeeQuestionInd, professionalQuestionInd } = values;
  if (!proInvolvement) return {};

  const firmEmployeeErrors = firmEmployeeQuestionInd ? validateFirmEmployee(proInvolvement) : {};
  const professionalErrors = professionalQuestionInd ? validateProfessional(proInvolvement) : {};

  const errors = {
    ...firmEmployeeErrors,
    ...professionalErrors,
  };

  return Object.keys(errors).length > 0 ? errors : {};
};

const validation = (values: EmploymentV2FormData): AddressValidationData | ErrorsDictionary | {} => {
  const { employerAddress, emplUID } = values;

  const radioButtonErrors = requiredValidation(values, ['firmEmployeeQuestionInd', 'professionalQuestionInd']);
  const proInvolvementErrors = proInvolvementValidation(values);
  const occupationErrors =
    emplUID === null
      ? { _error: { type: 'selectMainOccupation.required' } }
      : {
          employerAddress: addressValidation(employerAddress),
          employerPhoneNumber: employmentPhoneValidation(values),
          ...startOccupationDateValidation(values),
        };

  return {
    ...radioButtonErrors,
    ...(Object.keys(proInvolvementErrors).length > 0 && { proInvolvement: proInvolvementErrors }),
    ...occupationErrors,
  };
};

export default validation;
